import {securedApiRequest} from "./SecuredApi"

/**
 * Toggle a driver’s availability on the server.
 * @param {string} email
 * @param {boolean} isAvailable
 */
export const toggleAvailability = async (email, isAvailable) => {
    return securedApiRequest(
        "/is-available/",
        "POST",
        { email, isAvailable }
    );
};

/**
 * Fetches notifications for a given email address.
 *
 * This asynchronous function encodes the provided email address to ensure it is
 * safe for use in a URL. It then makes a GET request to the secured API endpoint
 * to retrieve notifications associated with the email.
 *
 * @param {string} email - The email address for which to fetch notifications.
 * @returns {Promise<Object>} A promise that resolves to the notifications data.
 *                             Rejects with an error if the API request fails.
 */
export const fetchNotifications = async (email) => {
    const encodedEmail = encodeURIComponent(email);
    return securedApiRequest(`/notifications/?email=${encodedEmail}`, "GET");
};

/**
 * Fetch all trips for a driver by email.
 * Backend expects a query param ?email=…
 */
export const fetchAllTrips = async () => {
    return securedApiRequest(`/get-all-driver-trips/`, "GET");
};

/**
 * Fetch trip details for a given reference.
 * @param {string} tripReference
 */
export const fetchTripDetails = async (tripReference) => {
    return securedApiRequest(`/get-trip-details/${tripReference}/`, "GET");
};

/**
 * Update trip status for a given reference.
 * @param {string} tripReference
 * @param {string} status
 */
export const updateTripStatus = async (tripReference, status) => {
    return securedApiRequest(
        `/update-trip-status/`,
        "POST",
        { reference: tripReference, status }
    );
};

/**
 * Fetch available trips for a driver.
 */
export const fetchAvailableTrips = async () => {
    return securedApiRequest(
        `/get-available-trips/`,
        'GET',
        null,
        true
    );
};

/**
 * Accept a trip by reference for a driver.
 * @param {string} email
 * @param {string} reference
 */
export const acceptTrip = async (email, reference) => {
    return securedApiRequest(
        '/accept-trip/',
        'POST',
        { email, reference },
        true
    );
};

/**
 * Reject a trip by reference for a driver.
 * @param {string} email
 * @param {string} reference
 */
export const rejectTrip = async (email, reference) => {
    return securedApiRequest(
        '/reject-trip/',
        'POST',
        { email, reference },
        true
    );
};

/**
 * Fetch earnings data for a driver.
 * @returns {Promise<{trips: Array}>}
 */
export const fetchDriverEarnings = async () => {
    return securedApiRequest(
        '/driver-earnings/',
        'GET',
        null,
        true
    );
};

/**
 * Submit a trip cancellation reason.
 * @param {string} reference - Trip reference
 * @param {string} reason - Reason for cancellation
 * @returns {Promise<Object>} Server response
 */
export const submitCancelReason = async (reference, reason) => {
    return securedApiRequest(
        "/cancel-trip-reason/",
        "POST",
        { reference, reason }
    );
};
